import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
	Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useUploadFileMutation } from "../../Api/Mutations/useUploadFileMutation";

type Props = {
	open: boolean;
	onClose: () => void;
	defaultFolderPath?: string | null;
};

const normalizeDefaultPath = (path?: string | null): string => {
	if (!path || path === "codelists") return "";
	if (path.startsWith("codelists/")) return path.replace("codelists/", "");
	return path;
};

export const FileUploadDialog: React.FC<Props> = ({
	open,
	onClose,
	defaultFolderPath,
}) => {
	const [file, setFile] = useState<File | null>(null);
	const [folderPath, setFolderPath] = useState(
		normalizeDefaultPath(defaultFolderPath),
	);

	useEffect(() => {
		if (open) {
			setFolderPath(normalizeDefaultPath(defaultFolderPath));
		}
	}, [open, defaultFolderPath]);

	const uploadMutation = useUploadFileMutation();

	const onDrop = useCallback((acceptedFiles: File[]) => {
		if (acceptedFiles.length > 0) {
			setFile(acceptedFiles[0]);
		}
	}, []);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		multiple: false,
		accept: {
			"text/csv": [".csv"],
			"application/vnd.ms-excel": [".xls"],
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
				".xlsx",
			],
			"text/xml": [".xml"],
			"application/xml": [".xml"],
			"application/json": [".json"],
		},
	});

	const handleClose = () => {
		setFile(null);
		setFolderPath(normalizeDefaultPath(defaultFolderPath));
		onClose();
	};

	const handleUpload = async () => {
		if (!file || !folderPath) return;

		await uploadMutation.mutateAsync({
			file,
			folderPath: folderPath.startsWith("codelists/")
				? folderPath.replace("codelists/", "")
				: folderPath,
		});

		handleClose();
	};

	const isValid = file && folderPath.trim().length > 0;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Typography variant="h6">Nahrát soubor</Typography>
					<IconButton onClick={handleClose} size="small">
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent>
				<Box sx={{ mb: 3 }}>
					<TextField
						fullWidth
						label="Cílová složka"
						value={folderPath}
						onChange={(e) => setFolderPath(e.target.value)}
						placeholder="např. nsure/vehicles"
						helperText="Složka bude automaticky vytvořena pod codelists/"
						size="small"
						sx={{ mt: 1, mb: 3 }}
					/>

					<Box
						{...getRootProps()}
						sx={{
							border: 2,
							borderStyle: "dashed",
							borderColor: isDragActive ? "primary.main" : "divider",
							borderRadius: 2,
							p: 4,
							textAlign: "center",
							cursor: "pointer",
							backgroundColor: isDragActive
								? "action.hover"
								: "background.paper",
							transition: "all 0.2s ease",
							"&:hover": {
								borderColor: "primary.main",
								backgroundColor: "action.hover",
							},
						}}
					>
						<input {...getInputProps()} />
						{file ? (
							<Box>
								<InsertDriveFileIcon
									sx={{ fontSize: 48, color: "primary.main", mb: 1 }}
								/>
								<Typography variant="body1" fontWeight={600}>
									{file.name}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{(file.size / 1024).toFixed(1)} KB
								</Typography>
							</Box>
						) : (
							<Box>
								<CloudUploadIcon
									sx={{ fontSize: 48, color: "action.active", mb: 1 }}
								/>
								<Typography variant="body1">
									{isDragActive
										? "Pusťte soubor zde..."
										: "Přetáhněte soubor nebo klikněte pro výběr"}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									Podporované formáty: CSV, XLS, XLSX, XML, JSON
								</Typography>
							</Box>
						)}
					</Box>
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button variant="text" onClick={handleClose}>
					Zrušit
				</Button>
				<Button
					variant="text"
					onClick={handleUpload}
					disabled={!isValid || uploadMutation.isPending}
					startIcon={
						uploadMutation.isPending ? (
							<CircularProgress size={16} color="inherit" />
						) : undefined
					}
				>
					{uploadMutation.isPending ? "Nahrávám..." : "Nahrát"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
